export function updateList<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id,
  fn: (list: T[], entry: T) => T[] = (_list, _entry) => [..._list, _entry]
): T[] {
  let newList = []
  let updated = false

  for (const value of list) {
    if (compare(value, entry)) {
      newList.push(entry)
      updated = true
    } else {
      newList.push(value)
    }
  }

  // Entry was not found so we call the function that modifies the list somehow (append, prepend, whatever)
  if (updated === false) {
    newList = fn(list, entry)
  }

  return newList
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry.
 * If no matching entry is found then the entry is appended.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateOrAppend<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
): T[] {
  return updateList(list, entry, compare, (_list, _entry) => [..._list, _entry])
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry.
 * If no matching entry is found then the entry is not added to the list.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateOrDiscard<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
): T[] {
  return updateList(list, entry, compare, _list => _list)
}

/**
 * Returns a new list in which a specific entry (equal check is performed by the given function) is replaced by the given entry.
 * If no matching entry is found then the entry is prepended.
 *
 * @param list List to operate on
 * @param entry Entry to update
 * @param compare By default the function will check if the ids are the same
 */
export function updateOrPrepend<T>(
  list: T[],
  entry: T,
  compare: (a: T, b: T) => boolean = (a: any, b: any) => a.id === b.id
): T[] {
  return updateList(list, entry, compare, (_list, _entry) => [_entry, ..._list])
}

/**
 * Returns a new list which doesn't contain the given entry (equal check is performed by the given function)
 *
 * @param list List to operate on
 * @param entries Entries to remove
 * @param compare Function that compares two objects
 */
export function withoutEntries<T>(
  list: T[],
  entries: Array<Partial<T>>,
  compare: (a: T, b: Partial<T>) => boolean = (a: any, b: any) => a.id === b.id
): T[] {
  const newList = []

  for (const item of list) {
    for (const entry of entries) {
      // Push the entry if it is not equal to the given entry
      if (compare(item, entry) === false) {
        newList.push(item)
      }
    }
  }

  return newList
}

/**
 * Returns a new list which doesn't contain the given entry (equal check is performed by the given function)
 *
 * @param list List to operate on
 * @param entry Entry to remove
 * @param compare Function that compares two objects
 */
export function withoutEntry<T, K extends keyof T>(
  list: T[],
  entry: Partial<T>,
  compare: (a: T, b: Partial<T>) => boolean = (a: any, b: any) => a.id === b.id
): T[] {
  const newList = []

  for (const item of list) {
    // Push the entry if it is not equal to the given entry
    if (compare(item, entry) === false) {
      newList.push(item)
    }
  }

  return newList
}
